import { useGoogleLogin } from '@react-oauth/google';
import { MouseEventHandler, ReactNode } from 'react';
import { IconSpinner } from '../Icon';
import { Color } from '../../lib/types.ts';
import { cn } from '../../lib/helpers.ts';

interface ButtonProps {
    color: Color | 'product' | 'opaque';
    variant?: 'primary' | 'secondary';
    title?: string;
    children: ReactNode;
    className?: string;
    onClick?: MouseEventHandler;
    isLoading?: boolean;
    loadingText?: string;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    tabIndex?: number;
}

export function Button({
    color,
    variant = 'primary',
    title,
    className,
    onClick,
    isLoading = false,
    loadingText,
    disabled,
    children,
    type = 'button',
    tabIndex,
}: ButtonProps) {
    const colorVariants = {
        product: {
            primary: cn('bg-product-500', !disabled && 'hover:bg-product-600'),
            secondary: cn(
                'text-product-600 bg-product-100',
                !disabled && 'hover:bg-product-200'
            ),
        },
        blue: {
            primary: cn('bg-blue-500', !disabled && 'hover:bg-blue-600'),
            secondary: cn(
                'text-blue-600 bg-blue-100',
                !disabled && 'hover:bg-blue-200'
            ),
        },
        green: {
            primary: cn('bg-green-500', !disabled && 'hover:bg-green-600'),
            secondary: cn(
                'text-green-600 bg-green-100',
                !disabled && 'hover:bg-green-200'
            ),
        },
        orange: {
            primary: cn('bg-orange-500', !disabled && 'hover:bg-orange-600'),
            secondary: cn(
                'text-orange-600 bg-orange-100',
                !disabled && 'hover:bg-orange-200'
            ),
        },
        red: {
            primary: cn('bg-red-500', !disabled && 'hover:bg-red-600'),
            secondary: cn(
                'text-red-600 bg-red-100',
                !disabled && 'hover:bg-red-200'
            ),
        },
        turquoise: {
            primary: cn(
                'bg-turquoise-500',
                !disabled && 'hover:bg-turquoise-600'
            ),
            secondary: cn(
                'text-turquoise-600 bg-turquoise-100',
                !disabled && 'hover:bg-turquoise-200'
            ),
        },
        purple: {
            primary: cn('bg-purple-500', !disabled && 'hover:bg-purple-600'),
            secondary: cn(
                'text-purple-600 bg-purple-100',
                !disabled && 'hover:bg-purple-200'
            ),
        },
        rose: {
            primary: cn('bg-rose-500', !disabled && 'hover:bg-rose-600'),
            secondary: cn(
                'text-rose-600 bg-rose-100',
                !disabled && 'hover:bg-rose-200'
            ),
        },
        opaque: {
            primary: cn(
                'bg-white/10 text-white',
                !disabled && 'hover:bg-white/20'
            ),
            secondary: cn(
                'text-white bg-white/10',
                !disabled && 'hover:bg-white/20'
            ),
        },
    };

    return (
        <button
            type={type}
            title={title}
            className={cn(
                'flex items-center justify-center rounded-xl px-4 py-3 text-sm font-medium',
                'cursor-pointer',
                'transition-colors',
                variant === 'primary' && 'text-white',
                colorVariants[color][variant],
                (isLoading || disabled) && 'cursor-not-allowed',
                className
            )}
            disabled={isLoading || disabled}
            onClick={onClick}
            tabIndex={tabIndex}
        >
            {/* Keep a stable node tree. Chrome Translate wraps text in <font>
                and insertBefore then throws NotFoundError. */}
            <IconSpinner
                size={20}
                className={cn(
                    'animate-spin',
                    loadingText !== undefined && 'mr-2',
                    !isLoading && 'hidden'
                )}
            />
            <span className={isLoading ? 'hidden' : undefined}>{children}</span>
            <span className={!isLoading ? 'hidden' : undefined}>
                {loadingText}
            </span>
        </button>
    );
}

export function ButtonGoogleLogin() {
    const googleLogin = useGoogleLogin({
        onSuccess: (tokenResponse) => console.log(tokenResponse),
    });

    return (
        <button
            className="flex items-center justify-center rounded-xl border border-cloud-300 px-4 py-3 font-medium"
            onClick={() => googleLogin()}
        >
            {/*<FcGoogle size={24} className="me-3" />*/}
            Přihlásit se pomocí Google
        </button>
    );
}
